import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react'
import moment from 'moment'
import { Table, Space, Select, Button, Modal } from 'antd'
import { RECORD_STATUS } from '@qflow/constants'

import { FormItemWrapper } from 'admin/lib/Forms'

import { Layout } from 'admin/lib/Layouts'
import { ScreenSpin } from 'admin/lib/ScreenSpin'
import './InTray.css'
import { calculateAssignmentOptions } from 'admin/lib/assigneeFunctions'

import { useApi, useApiQuery, Queries } from 'admin/lib/Api'
import { ResponsiveWrapper } from 'admin/lib/Common/StyledElements'
import { reducer } from 'admin/lib/InTray/FilterHelpers'
import { Filters } from 'admin/lib/InTray/Filters'
import { ResendToQCRModal } from 'admin/lib/InTray/ResendToQCRModal'
import { columns, columnsForSuperUser } from 'admin/lib/InTray/TableHelpers'

export function InTray() {
  // Update favicon for InTray
  useEffect(() => {
    let link = document.querySelector("link[rel~='icon']")
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.getElementsByTagName('head')[0].appendChild(link)
    }
    link.href = 'assets/favicon-intray.ico'
  }, [])

  const [
    {
      startDate,
      endDate,
      accountIds,
      projectIds,
      statuses,
      modules,
      companyName,
      movementId,
      recordsLoaded,
      pagesLoaded,
      assignees,
      countries,
      templateNames,
      continuationToken,
      reloadRecords
    },
    dispatch
  ] = useReducer(reducer, {
    startDate: moment().startOf('day').subtract(14, 'd').format(),
    endDate: moment().endOf('day').format(),
    accountIds: [],
    projectIds: [],
    statuses: [],
    modules: [],
    assignees: [],
    countries: [],
    companyName: null,
    movementId: null,
    templateNames: [],
    recordsLoaded: [],
    pagesLoaded: 1,
    continuationToken: null,
    reloadRecords: 0
  })

  const user = Queries.useUserSelf()
  const api = useApi()
  const users = Queries.useQflowUsers()

  const [sortOrder, setSortOrder] = useState(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedAssignee, setSelectedAssignee] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageContinuationToken, setPageContinuationToken] = useState({
    1: null
  })
  const [assignButtonDisabled, setAssignButtonDisabled] = useState(false)
  const [resendButtonDisabled, setResendButtonDisabled] = useState(false)

  const [newStatus, setNewStatus] = useState(RECORD_STATUS.FAILED)
  const newStatusRef = useRef()
  newStatusRef.current = newStatus

  const [bulkStatusButtonDisabled, setBulkStatusButtonDisabled] =
    useState(false)
  const [
    openSelectedRecordsButtonDisabled,
    setOpenSelectedRecordsButtonDisabled
  ] = useState(false)

  const [resendWarningModuleOpen, setResendWarningModuleOpen] = useState(false)
  const [resendTemplateInput, setResendTemplateInput] = useState('')

  const SIMILARITY_THRESHOLD = 1.1 // Effectively Switching This Off
  const { loaded: countLoaded, data: countData } = useApiQuery(
    useCallback(
      api => {
        // Keep this console.log because we need reloadRecords in the dependancy list to trigger a refresh
        console.log(reloadRecords)
        return api.getInTrayCount({
          startDateIso: moment(startDate).local().format(),
          endDateIso: moment(endDate).local().format(),
          accountIds,
          projectIds,
          statuses,
          modules,
          companyName,
          movementId,
          assignees,
          templateNames,
          countries
        })
      },
      [
        reloadRecords,
        startDate,
        endDate,
        accountIds,
        projectIds,
        statuses,
        modules,
        companyName,
        movementId,
        assignees,
        templateNames,
        countries
      ]
    )
  )

  const { loaded, data } = useApiQuery(
    useCallback(
      api => {
        // Keep this console.log because we need reloadRecords in the dependancy list to trigger a refresh
        console.log(reloadRecords)
        setSelectedRowKeys([])
        return api.getInTray({
          startDateIso: moment(startDate).local().format(),
          endDateIso: moment(endDate).local().format(),
          accountIds,
          projectIds,
          statuses,
          modules,
          companyName,
          movementId,
          assignees,
          templateNames,
          countries,
          continuationToken
        })
      },
      [
        reloadRecords,
        startDate,
        endDate,
        accountIds,
        projectIds,
        statuses,
        modules,
        companyName,
        movementId,
        assignees,
        templateNames,
        countries,
        continuationToken,
        setSelectedRowKeys
      ]
    )
  )

  const records =
    data?.records == null
      ? [...recordsLoaded]
      : [...recordsLoaded, ...data.records]

  if (data?.hasNextPage) {
    records.push({})
  }

  const getColumns = user.data?.isSuper ? columnsForSuperUser : columns

  // Add sorting info to columns if total records <= 300
  if (countData !== null && records.length <= 300) {
    getColumns.forEach(col => {
      if (col.dataIndex) {
        if (Array.isArray(col.dataIndex)) {
          col.sorter = (a, b) =>
            (a[col.dataIndex[0]] && a[col.dataIndex[0]][col.dataIndex[1]]
              ? a[col.dataIndex[0]][col.dataIndex[1]]
              : ''
            ).localeCompare(
              b[col.dataIndex[0]] && b[col.dataIndex[0]][col.dataIndex[1]]
                ? b[col.dataIndex[0]][col.dataIndex[1]]
                : ''
            )
        } else if (col.dataIndex === 'attachmentPages') {
          col.sorter = (a, b) =>
            parseInt(a[col.dataIndex]) - parseInt(b[col.dataIndex])
        } else {
          col.sorter = (a, b) =>
            (a[col.dataIndex] || '').localeCompare(b[col.dataIndex] || '')
        }
        col.sortOrder =
          sortOrder?.field === col.dataIndex ? sortOrder?.order : null
      }
    })
  } else {
    // Remove any sorting info if total records > 300
    getColumns.forEach(col => {
      if (col.dataIndex) {
        col.sorter = false
        col.sortOrder = null
      }
    })
  }

  const handleTableChange = (pagination, filters, sorter) => {
    const newSortOrder = {
      order: sorter.order,
      field: sorter.field
    }
    setSortOrder(newSortOrder)
    handlePaginationChange(pagination)
  }

  const onSelectChange = newSelectedRowKeys => {
    setSelectedRowKeys(newSelectedRowKeys)
  }

  const handleSelectedAssigneeChange = value => {
    setSelectedAssignee(value)
  }

  const disableRowSelectionCriteria = status => {
    if (user.data && user.data.isSuper) {
      return false
    }
    return status === RECORD_STATUS.VOID
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: record => ({
      disabled: disableRowSelectionCriteria(record.status)
    }),
    selections: [Table.SELECTION_INVERT]
  }

  const handleClickAssign = async () => {
    // Disable AssignButton to prevent double click
    setAssignButtonDisabled(true)

    const { assignTo, selectedRecords } = collectUpdateAssigneeData(
      selectedRowKeys,
      records,
      selectedAssignee
    )

    // Store starting number
    const startingNumberOfRecords = selectedRecords.length
    // Reload records for the current page
    const refetchData = await api.getInTray({
      startDateIso: moment(startDate).local().format(),
      endDateIso: moment(endDate).local().format(),
      accountIds,
      projectIds,
      statuses,
      modules,
      companyName,
      movementId,
      assignees,
      countries,
      templateNames,
      continuationToken: pageContinuationToken[currentPage]
    })

    const refetchDataObj = refetchData[0].records.reduce((result, item) => {
      result[item.recordId] = item.assignee
      return result
    }, {})

    // Check to see if the assignment of these records have changed since last load
    // Only keep records with unchanged assignments
    const unchangedSelectedRecords = selectedRecords.filter(record => {
      return refetchDataObj[record.recordId] === record.assignee
    })
    // Store number of unchanged selected records
    const finalNumberOfRecords = unchangedSelectedRecords.length
    if (finalNumberOfRecords > 0) {
      const [, , error] = await api.updateAssignee(
        assignTo,
        unchangedSelectedRecords
      )
      if (error) {
        console.error(error)
        const status = error.response?.status

        // Use Starting and Final numbers in the 500 message
        if (status === 500) {
          return Modal.error({
            title: 'Submit Failed',
            content: (
              <div>
                <p>
                  Records successfully assigned:{' '}
                  {error.response.data.assignedCount}
                </p>
                <p>
                  Records failed:{' '}
                  {error.response.data.notAssignedCount +
                    (startingNumberOfRecords - finalNumberOfRecords)}
                </p>
              </div>
            ),
            centered: true,
            onOk: handleRefreshRecords
          })
        } else {
          return Modal.error({
            title: 'Submit Failed',
            content: String(error),
            centered: true
          })
        }
      }
    }

    // If Start and Final numbers differ show Modal
    if (startingNumberOfRecords > finalNumberOfRecords) {
      return Modal.error({
        title: 'Some records have been updated',
        content: (
          <div>
            <p>
              One or more records have had their assignment changed since you
              last refreshed this page. We have only updated unchanged records.
            </p>
            <p>Records successfully assigned: {finalNumberOfRecords}</p>
            <p>
              Records failed: {startingNumberOfRecords - finalNumberOfRecords}
            </p>
          </div>
        ),
        centered: true,
        onOk: handleRefreshRecords
      })
    }

    handleRefreshRecords()
  }

  const handleStatusChange = value => {
    setNewStatus(value)
  }

  const showBulkStatusModal = () => {
    return Modal.confirm({
      title: 'Bulk Status Update',
      content: (
        <div>
          <div>
            <label htmlFor="newStatus">Select new status</label>
          </div>
          <Select
            id="newStatus"
            style={{ width: '15rem' }}
            defaultValue={newStatusRef.current}
            onChange={handleStatusChange}
            options={[
              { value: RECORD_STATUS.FAILED, label: RECORD_STATUS.FAILED },
              {
                value: RECORD_STATUS.ESCALATED,
                label: RECORD_STATUS.ESCALATED
              },
              {
                value: RECORD_STATUS.PUBLISHED,
                label: RECORD_STATUS.PUBLISHED
              },
              {
                value: RECORD_STATUS.AUTOPUBLISHED,
                label: RECORD_STATUS.AUTOPUBLISHED
              },
              { value: RECORD_STATUS.VOID, label: RECORD_STATUS.VOID }
            ]}
          ></Select>
          <p style={{ marginTop: '14px' }}>
            You are about to change the Status of:
            <br />
            <strong>
              {selectedRowKeys.length} record
              {selectedRowKeys.length > 1 ? 's' : ''}
            </strong>
            .
          </p>
          <p style={{ marginBottom: '0' }}>
            Are you sure? Click OK to continue.
          </p>
        </div>
      ),
      centered: true,
      onOk: handleClickBulkStatus
    })
  }

  const showResendWarning = () => {
    setResendWarningModuleOpen(true)
  }

  const handleClickBulkStatus = async () => {
    // Disable ResendButton to prevent double click
    setBulkStatusButtonDisabled(true)

    const assignableRecords = records.filter(record => {
      return selectedRowKeys.includes(record.recordId)
    })

    const selectedRecords = assignableRecords.map(record => ({
      recordId: record.recordId,
      projectId: record.project.projectId,
      moduleName: record.moduleName
    }))

    if (selectedRecords.length > 0) {
      const [, , error] = await api.bulkStatusUpdate(
        newStatusRef.current,
        selectedRecords
      )
      if (error) {
        console.error(error)
        return Modal.error({
          title: 'Bulk Status Update Failed',
          content: String(error),
          centered: true,
          onOk: handleRefreshRecords
        })
      } else {
        return Modal.info({
          title: `${selectedRecords.length} record${
            selectedRecords.length > 1 ? 's' : ''
          } updated`,
          content: (
            <div>
              <p>
                {selectedRecords.length} record
                {selectedRecords.length > 1 ? 's have' : ' has'} been updated to
                have a Status of {newStatusRef.current}.
              </p>
            </div>
          ),
          centered: true,
          onOk: handleRefreshRecords
        })
      }
    }
  }

  const handleClickResend = async () => {
    // Close Modal and Disable ResendButton to prevent double click
    setResendWarningModuleOpen(false)
    setResendButtonDisabled(true)

    const assignableRecords = records.filter(record => {
      return selectedRowKeys.includes(record.recordId)
    })

    const selectedRecords = assignableRecords.map(record => ({
      recordId: record.recordId,
      projectId: record.project.projectId
    }))

    if (selectedRecords.length > 0) {
      const [, , error] = await api.resendToQCR(
        selectedRecords,
        resendTemplateInput
      )
      if (error) {
        console.error(error)
        return Modal.error({
          title: 'Resend Failed',
          content: String(error),
          centered: true,
          onOk: handleRefreshRecords
        })
      } else {
        return Modal.info({
          title: `${selectedRecords.length} record${
            selectedRecords.length > 1 ? 's' : ''
          } resent to QCR`,
          content: (
            <div>
              <p>
                {selectedRecords.length} record
                {selectedRecords.length > 1 ? 's have' : ' has'} been resent to
                QCR to be requeued.
              </p>
              <p>
                Please wait a few minutes before editing{' '}
                {selectedRecords.length > 1 ? 'these records' : 'this record'}.
              </p>
            </div>
          ),
          centered: true,
          onOk: handleRefreshRecords
        })
      }
    }
  }

  const openSelectedRecords = () => {
    // Disable openSelectedRecords Button to prevent double click
    setOpenSelectedRecordsButtonDisabled(true)
    selectedRowKeys.forEach(recordId => {
      document.querySelector('tr[data-row-key="' + recordId + '"] a').click()
    })

    // Reenable openSelectedRecords Button
    setTimeout(() => {
      setOpenSelectedRecordsButtonDisabled(false)
    }, 500)
  }

  function collectUpdateAssigneeData(selectedRowKeys, records, assignTo) {
    const assignableRecords = records.filter(record => {
      return selectedRowKeys.includes(record.recordId)
    })

    const selectedRecords = assignableRecords.map(record => ({
      projectId: record.project.projectId,
      recordId: record.recordId,
      moduleName: record.moduleName,
      assignee: record.assignee
    }))

    return { assignTo, selectedRecords }
  }

  function handlePageChange(page, pageSize) {
    if (page <= pagesLoaded) {
      return
    }
    const tempPageContinuationToken = pageContinuationToken
    tempPageContinuationToken[page] = data?.continuationToken
    setPageContinuationToken(tempPageContinuationToken)

    dispatch({
      type: 'setPageChanges',
      data: {
        pagesLoaded: page,
        recordsLoaded: data?.records,
        continuationToken: data?.continuationToken
      }
    })
  }

  function handlePaginationChange(pagination) {
    setCurrentPage(pagination.current)
  }

  function handleRefreshRecords() {
    setCurrentPage(1)
    setAssignButtonDisabled(false)
    setBulkStatusButtonDisabled(false)
    setOpenSelectedRecordsButtonDisabled(false)
    setResendButtonDisabled(false)
    dispatch({ type: 'onRefreshClick' })
  }

  function determineBtnTitle(selectedRowsCount, buttonName) {
    if (selectedRowsCount > 300) {
      return 'Please select less than 300 records'
    }
    if (selectedRowsCount > 50 && buttonName === 'openSelectedRecords') {
      return 'Please select less than 50 records'
    }
    if (selectedRowsCount === 0) {
      return 'Please select at least 1 record'
    } else {
      switch (buttonName) {
        case 'assign':
          return 'Click to assign selected user to records'
        case 'bulkStatus':
          return 'Click to change Status of selected records'
        case 'openSelectedRecords':
          return 'Click to open selected records'
        case 'resend':
          return 'Click to resend records to QCR'
      }
    }
  }

  const sumOfSelectedPages = useMemo(() => {
    let sumOfPages = 0
    selectedRowKeys.forEach(recordId => {
      const record = data?.records.find(r => r.recordId === recordId)
      if (record) {
        sumOfPages += record.attachmentPages
      }
    })
    return sumOfPages
  }, [data?.records, selectedRowKeys])

  return (
    <Layout>
      <div>
        <Filters
          accountIds={accountIds}
          projectIds={projectIds}
          users={users}
          assignees={assignees}
          dispatch={dispatch}
          handleRefreshRecords={handleRefreshRecords}
          disabled={countData === null || countData === 0}
        />
        <ResponsiveWrapper style={{ margin: '0 2rem' }}>
          {!user.isFetched && <ScreenSpin id="userLoadingSpinner" />}
          <Space direction="horizontal" id="action-bar">
            {user.isFetched && !countLoaded && (
              <div className="total-records" id="totalRecordsQuerying">
                Total: Querying...
              </div>
            )}
            {user.isFetched && countLoaded && (
              <div className="total-records" id="totalRecordsCount">
                Total:{' '}
                <span style={{ fontWeight: '700' }}>
                  {typeof countData === 'number' && (
                    <>
                      {countData || 0} record{countData === 1 ? '' : 's'}
                    </>
                  )}
                  {typeof countData === 'object' && (
                    <>
                      {countData.records || 0} record
                      {countData.records === 1 ? '' : 's'}
                      <span>{` (${countData.pages} page${countData.pages === 1 ? '' : 's'})`}</span>
                    </>
                  )}
                </span>
              </div>
            )}
            {selectedRowKeys.length > 0 && (
              <div className="total-records" id="totalRecordsSelected">
                Selected:{' '}
                <span>
                  {selectedRowKeys.length} record
                  {selectedRowKeys.length === 1 ? '' : 's'}
                </span>
                <span>{` (${sumOfSelectedPages} pages)`}</span>
              </div>
            )}
            <Space>
              {users.data && (
                <FormItemWrapper>
                  <label htmlFor="selectAssignee">Select assignee</label>
                  <div>
                    <Select
                      id="selectAssignee"
                      style={{ width: '20rem' }}
                      onChange={handleSelectedAssigneeChange}
                      placeholder="Assign selected records to user"
                      showSearch={true}
                      className="no-right-border-radius"
                      disabled={
                        selectedRowKeys.length > 300 ||
                        selectedRowKeys.length === 0 ||
                        assignButtonDisabled
                      }
                    >
                      {calculateAssignmentOptions(users.data)}
                    </Select>
                    <Button
                      type="primary"
                      style={{ borderRadius: '0 5px 5px 0', lineHeight: '1' }}
                      onClick={handleClickAssign}
                      disabled={
                        selectedRowKeys.length > 300 ||
                        selectedRowKeys.length === 0 ||
                        selectedAssignee?.length === 0 ||
                        assignButtonDisabled
                      }
                      loading={assignButtonDisabled}
                      title={determineBtnTitle(
                        selectedRowKeys.length,
                        'assign'
                      )}
                    >
                      {assignButtonDisabled ? 'Assigning' : 'Assign'}
                    </Button>
                  </div>
                </FormItemWrapper>
              )}

              {user.data && user.data.isSuper && (
                <>
                  <FormItemWrapper>
                    <label>&nbsp;</label>
                    <Button
                      type="secondary"
                      onClick={showResendWarning}
                      disabled={
                        selectedRowKeys.length > 300 ||
                        selectedRowKeys.length === 0 ||
                        resendButtonDisabled
                      }
                      loading={resendButtonDisabled}
                      title={determineBtnTitle(
                        selectedRowKeys.length,
                        'resend'
                      )}
                    >
                      {resendButtonDisabled
                        ? 'Resending to QCR'
                        : 'Resend to QCR'}
                    </Button>
                  </FormItemWrapper>
                  <FormItemWrapper>
                    <label>&nbsp;</label>
                    <Button
                      type="secondary"
                      onClick={showBulkStatusModal}
                      disabled={
                        selectedRowKeys.length > 300 ||
                        selectedRowKeys.length === 0 ||
                        bulkStatusButtonDisabled
                      }
                      loading={bulkStatusButtonDisabled}
                      title={determineBtnTitle(
                        selectedRowKeys.length,
                        'bulkStatus'
                      )}
                    >
                      {resendButtonDisabled
                        ? 'Bulk status updating'
                        : 'Bulk status update'}
                    </Button>
                  </FormItemWrapper>
                </>
              )}

              <FormItemWrapper>
                <label>&nbsp;</label>
                <Button
                  type="secondary"
                  onClick={openSelectedRecords}
                  disabled={
                    selectedRowKeys.length > 50 ||
                    selectedRowKeys.length === 0 ||
                    openSelectedRecordsButtonDisabled
                  }
                  title={determineBtnTitle(
                    selectedRowKeys.length,
                    'openSelectedRecords'
                  )}
                >
                  Open selected records
                </Button>
              </FormItemWrapper>
            </Space>
          </Space>
          {user.isFetched && !loaded && (
            <ScreenSpin id="recordsLoadingSpinner" />
          )}
          {user.isFetched && loaded && (
            <Table
              id="recordsTable"
              dataSource={records}
              rowSelection={rowSelection}
              rowKey={row => row.recordId}
              rowClassName={row =>
                row.similarity >= SIMILARITY_THRESHOLD &&
                row.recordId !== row.similarRecordId
                  ? 'possible-duplicate'
                  : ''
              }
              columns={getColumns}
              onChange={handleTableChange}
              pagination={{
                pageSize: 300,
                onChange: handlePageChange,
                defaultCurrent: pagesLoaded,
                showSizeChanger: false
              }}
              scroll={{ y: `calc(100vh - 480px)` }}
              sticky
              style={{
                width: '100%'
              }}
            />
          )}
        </ResponsiveWrapper>
      </div>
      <ResendToQCRModal
        isOpen={resendWarningModuleOpen}
        onClose={() => setResendWarningModuleOpen(false)}
        onSubmit={handleClickResend}
        resendTemplateInput={resendTemplateInput}
        setResendTemplateInput={setResendTemplateInput}
      />
    </Layout>
  )
}
